import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import "./ProjectDetail.css";
import Header from "./Header";
import Footer from "./Footer";
import ScrollArrow from "./ScrollArrow";
import {
  projects,
  isProjectEnabled,
  getEnabledProjectKeys,
} from "../data/projectData";

// Helper function to convert URLs in text to clickable links
const renderTextWithLinks = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return part;
  });
};

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if project exists and is enabled
  useEffect(() => {
    if (!projects[projectId] || !isProjectEnabled(projectId)) {
      navigate("/"); // Redirect to home if project doesn't exist or is disabled
      return;
    }
  }, [projectId, navigate]);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Calculate transform value based on device type
  const getTransformValue = () => {
    if (isMobile) {
      return `translateX(-${currentFeatureIndex * 100}%)`; // Full slide width on mobile
    } else {
      // Center the active slide by calculating viewport center offset
      const slideWidth = 800; // max-width from CSS
      const viewportCenter =
        typeof window !== "undefined" ? window.innerWidth / 2 : 800;
      const slideCenter = slideWidth / 2;
      const baseOffset = viewportCenter - slideCenter;
      const slideSpacing = slideWidth - 48; // 3rem margin-right converted to px
      const totalOffset = baseOffset - currentFeatureIndex * slideSpacing;
      return `translateX(${totalOffset}px)`;
    }
  };

  // Touch support for mobile devices
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentFeatureIndex < project.features.length - 1) {
      nextFeature();
    }
    if (isRightSwipe && currentFeatureIndex > 0) {
      prevFeature();
    }
  };

  // 3D Movement functions for tech cards
  const handleTechCardMouseMove = (e, techCardElement) => {
    if (!techCardElement) return;

    const rect = techCardElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -8; // Max 8deg tilt
    const rotateY = ((x - centerX) / centerX) * 8;

    // Calculate shine position (0-100%)
    const shineX = (x / rect.width) * 100;
    const shineY = (y / rect.height) * 100;

    // Calculate dynamic shadow offset based on cursor position
    const shadowOffsetX = -((x - centerX) / centerX) * 4; // Max 4px offset
    const shadowOffsetY = -((y - centerY) / centerY) * 4; // Max 4px offset

    techCardElement.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;

    // Update CSS custom properties for shine effect
    techCardElement.style.setProperty("--shine-x", `${shineX}%`);
    techCardElement.style.setProperty("--shine-y", `${shineY}%`);
    techCardElement.style.setProperty("--shine-opacity", "0.4");

    // Update CSS custom properties for dynamic shadows
    techCardElement.style.setProperty("--shadow-x", `${shadowOffsetX}px`);
    techCardElement.style.setProperty("--shadow-y", `${shadowOffsetY}px`);
  };

  const handleTechCardMouseLeave = (techCardElement) => {
    if (!techCardElement) return;

    techCardElement.style.transform =
      "perspective(800px) rotateX(0deg) rotateY(0deg) scale(1)";
    techCardElement.style.setProperty("--shine-opacity", "0");
    techCardElement.style.setProperty("--shadow-x", "0px");
    techCardElement.style.setProperty("--shadow-y", "0px");
  };

  // Get project order from enabled projects
  const projectOrder = getEnabledProjectKeys();
  const currentIndex = projectOrder.indexOf(projectId);
  const previousProject =
    currentIndex > 0 ? projectOrder[currentIndex - 1] : null;
  const nextProject =
    currentIndex < projectOrder.length - 1
      ? projectOrder[currentIndex + 1]
      : null;

  // Add body class for page styling
  useEffect(() => {
    document.body.classList.add("project-detail-page");
    return () => {
      document.body.classList.remove("project-detail-page");
    };
  }, []);

  // Reset feature index when project changes
  useEffect(() => {
    setCurrentFeatureIndex(0);
  }, [projectId]);

  // Scroll to top when component mounts or project changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [projectId]);

  // Page load animation
  useEffect(() => {
    // Reset page loaded state when project changes
    setIsPageLoaded(false);

    // Small delay to ensure page content is rendered, then trigger animation
    const timer = setTimeout(() => {
      setIsPageLoaded(true);
    }, 150); // 150ms delay for smooth transition

    return () => clearTimeout(timer);
  }, [projectId]);

  // Auto-scroll to content after 3 seconds
  useEffect(() => {
    const autoScrollTimer = setTimeout(() => {
      // Check if user is still at the top of the page before auto-scrolling
      const currentScrollY = window.scrollY;
      const isStillAtTop = currentScrollY < 100; // Consider "top" as first 100px

      if (!isStillAtTop) {
        // Don't auto-scroll if user has already scrolled away
        return;
      }

      const contentElement = document.querySelector(".project-detail-content");
      if (contentElement) {
        const elementTop = contentElement.offsetTop;
        const remInPixels = parseFloat(
          getComputedStyle(document.documentElement).fontSize
        );
        const targetTop = elementTop - 1 * remInPixels; // 2rem before the element

        // Custom slow scroll function
        const startTop = window.pageYOffset;
        const distance = targetTop - startTop;
        const duration = 2000; // 2 seconds for slower scroll
        let startTime = null;

        const slowScroll = (currentTime) => {
          if (startTime === null) startTime = currentTime;
          const timeElapsed = currentTime - startTime;
          const progress = Math.min(timeElapsed / duration, 1);

          // Easing function for smooth animation (ease-out)
          const ease = 1 - Math.pow(1 - progress, 3);

          window.scrollTo(0, startTop + distance * ease);

          if (progress < 1) {
            requestAnimationFrame(slowScroll);
          }
        };

        requestAnimationFrame(slowScroll);
      }
    }, 3000); // 4000ms = 4 seconds

    return () => clearTimeout(autoScrollTimer);
  }, [projectId]); // Re-run when project changes

  // Intersection Observer for scroll-based animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add visible class when element becomes visible
            entry.target.classList.add("visible");
          } else {
            // Remove visible class when element is not visible (for reset functionality)
            entry.target.classList.remove("visible");
          }
        });
      },
      {
        threshold: 0.08, // Trigger when 8% of element is visible
        rootMargin: "0px 0px -100px 0px", // Add some margin to trigger earlier/later
      }
    );

    // Additional scroll listener to reset all animations when at top
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const isAtTop = scrollY < 50; // Consider "top" as first 50px

      if (isAtTop) {
        // Reset all animations when user scrolls back to top (except Hero and Navigation)
        const projectContentChildren = document.querySelectorAll(
          ".project-detail-content > *:not(.project-detail-hero):not(.project-navigation)"
        );
        projectContentChildren.forEach((child) => {
          child.classList.remove("visible");
        });
      }
    };

    // Observe all sections except the hero and navigation
    const projectContentChildren = document.querySelectorAll(
      ".project-detail-content > *:not(.project-detail-hero):not(.project-navigation)"
    );
    projectContentChildren.forEach((child) => {
      observer.observe(child);
    });

    // Add scroll listener for top reset
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup function
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [projectId]); // Re-run when project changes

  const project = projects[projectId];

  if (!project) {
    return (
      <>
        <Header />
        <h1>Project not found</h1>
        <Link to="/" className="back-link">
          ← Back to Home
        </Link>
      </>
    );
  }

  // Feature slider navigation functions
  const nextFeature = () => {
    setCurrentFeatureIndex((prev) =>
      prev < project.features.length - 1 ? prev + 1 : prev
    );
  };

  const prevFeature = () => {
    setCurrentFeatureIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  // Check if buttons should be disabled
  const isAtStart = currentFeatureIndex === 0;
  const isAtEnd = currentFeatureIndex === project.features.length - 1;

  // Navigation functions with animation
  const navigateToProject = (targetProject) => {
    // First trigger fade-out animation
    setIsPageLoaded(false);

    // After a short delay, navigate to the new project
    setTimeout(() => {
      navigate(`/project/${targetProject}`);
    }, 300); // 300ms delay for fade-out effect
  };

  const handlePreviousProject = () => {
    if (previousProject) {
      navigateToProject(previousProject);
    }
  };

  const handleNextProject = () => {
    if (nextProject) {
      navigateToProject(nextProject);
    }
  };

  // Early return if project doesn't exist or is disabled
  if (!projects[projectId] || !isProjectEnabled(projectId)) {
    return null; // Component will redirect in useEffect
  }

  return (
    <>
      <Header />
      <div
        className={`project-detail-page-wrapper ${
          isPageLoaded ? "loaded" : ""
        }`}
      >
        <div className="project-detail-hero">
          <div className="project-detail-hero-content">
            {/* <div className="project-detail-category">{project.category}</div> */}
            <div className="project-detail-title">{project.title}</div>
            <p className="project-detail-subtitle">{project.subtitle}</p>
          </div>
        </div>
        <div className="project-detail-content">
          <div className="project-detail-media-section">
            <div className="project-summary-image-container">
              {project.isVideo ? (
                <video
                  className="project-summary-image"
                  src={project.image}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              ) : (
                <img
                  className="project-summary-image"
                  src={project.image}
                  alt={project.title}
                />
              )}
            </div>
          </div>

          <div className="project-detail-overview">
            <div className="project-detail-overview-content">
              <h2 className="section-eyebrow">Overview</h2>
              <p className="overview-description">{project.description}</p>
            </div>
          </div>

          <div className="content-grid">
            <section className="content-section challenge-section">
              <div className="section-header">
                <h3 className="section-title">The Challenge</h3>
              </div>
              <div className="section-content">
                <p>{project.challenge}</p>
              </div>
            </section>

            <section className="content-section solution-section">
              <div className="section-header">
                <h3 className="section-title">The Solution</h3>
              </div>
              <div className="section-content">
                <p>{renderTextWithLinks(project.solution)}</p>
              </div>
            </section>
          </div>

          <section className="features-section">
            <div className="section-container">
              <h3 className="section-eyebrow">Key Features</h3>
              <div className="features-slider">
                <div
                  className="features-slider-container"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div
                    className="features-track"
                    style={{
                      transform: getTransformValue(),
                    }}
                  >
                    {project.features.map((feature, index) => {
                      return (
                        <div
                          key={index}
                          className={`feature-slide ${
                            index === currentFeatureIndex ? "active" : ""
                          }`}
                        >
                          <div className="feature-card">
                            <div className="feature-image-container">
                              <img
                                src={
                                  feature.image ||
                                  "https://via.placeholder.com/400x300?text=Feature+Image"
                                }
                                alt={feature.name}
                                className="feature-image"
                              />
                            </div>
                            <div className="feature-content">
                              <h4 className="feature-name">{feature.name}</h4>
                              <p className="feature-description">
                                {feature.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Navigation container positioned at bottom right */}
                <div className="slider-nav-container">
                  <button
                    className={`slider-nav slider-nav-prev ${
                      isAtStart ? "disabled" : ""
                    }`}
                    onClick={prevFeature}
                    disabled={isAtStart}
                    aria-label="Previous feature"
                  >
                    <img
                      className="chevron-left"
                      src="https://www.svgrepo.com/show/522037/chevron-left.svg"
                      alt="Previous"
                      width="16"
                      height="16"
                    />
                  </button>

                  <button
                    className={`slider-nav slider-nav-next ${
                      isAtEnd ? "disabled" : ""
                    }`}
                    onClick={nextFeature}
                    disabled={isAtEnd}
                    aria-label="Next feature"
                  >
                    <img
                      className="chevron-right"
                      src="https://www.svgrepo.com/show/522041/chevron-right.svg"
                      alt="Next"
                      width="16"
                      height="16"
                    />
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="tech-section">
            <div className="section-container">
              <h3 className="section-eyebrow">Technologies Used</h3>
              <div className="tech-grid">
                {project.technologies.map((tech, index) => (
                  <div
                    key={index}
                    className="tech-card"
                    onMouseMove={(e) =>
                      handleTechCardMouseMove(e, e.currentTarget)
                    }
                    onMouseLeave={(e) =>
                      handleTechCardMouseLeave(e.currentTarget)
                    }
                  >
                    <div className="tech-card-shine"></div>
                    <span className="tech-name">{tech}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Project Navigation */}
          <section className="project-navigation">
            <div className="nav-container">
              {previousProject && (
                <div className="nav-link nav-previous">
                  <img
                    className="nav-arrow nav-arrow-back"
                    src="https://www.svgrepo.com/show/500777/back.svg"
                    alt="Previous"
                    width="20"
                    height="20"
                    onClick={handlePreviousProject}
                  />
                  <div className="nav-text">
                    <span className="nav-label">Previous Project</span>
                    <span
                      className="nav-title"
                      onClick={handlePreviousProject}
                      style={{ cursor: "pointer" }}
                    >
                      {projects[previousProject].title}
                    </span>
                  </div>
                </div>
              )}

              {nextProject && (
                <div className="nav-link nav-next">
                  <div className="nav-text">
                    <span className="nav-label">Next Project</span>
                    <span
                      className="nav-title"
                      onClick={handleNextProject}
                      style={{ cursor: "pointer" }}
                    >
                      {projects[nextProject].title}
                    </span>
                  </div>
                  <img
                    className="nav-arrow nav-arrow-next"
                    src="https://www.svgrepo.com/show/500945/next.svg"
                    alt="Next"
                    width="20"
                    height="20"
                    onClick={handleNextProject}
                  />
                </div>
              )}
            </div>
          </section>
        </div>{" "}
        {/* Closing project-detail-content */}
      </div>
      <Footer />
      <ScrollArrow />
    </>
  );
}
